<script setup>
import { computed, ref } from 'vue';
import CloseIcon from '@/js/core/components/icons/CloseIcon.vue';
import { useProfileStore } from '@/js/core/stores/profile-store';
import { usePersonMeta } from '@/js/core/composables/usePersonMeta';
import { DEFAULT_AUDIENCE_GROUP } from '@/js/core/utils/audience-group';
import { AUDIENCE_GROUP_DESCRIPTION } from '@/js/core/constants/audience-group-description';

const MAX_CONTACTS = 10;

const props = defineProps({
  person: {
    type: Object,
    required: true,
  },
});

const profileStore = useProfileStore();
const draftContacts = ref([]);
const isEditingContacts = ref(false);
const isSavingContacts = ref(false);
const contactsError = ref('');
const contactsFieldErrors = ref({});

const {
  departmentText,
  researchAreasText,
  titlesText,
  officesText,
  programsText,
  hasDirectoryBox,
  hasContactBox,
  phoneHref,
} = usePersonMeta(() => props.person);

const email = computed(() => props.person.email || '');
const emailHref = computed(() => `mailto:${email.value}`);
const audienceLabel = computed(() => props.person.audienceLabel || DEFAULT_AUDIENCE_GROUP);

const normalizedContacts = computed(() => {
  return (props.person.contacts ?? []).map(contact => ({
    uuid: contact.uuid,
    label: contact.label || '',
    value: contact.value || '',
  }));
});

const makeBlankContact = () => ({
  label: '',
  value: '',
  errors: { label: '', value: '' },
  inputErrors: { label: false, value: false },
});

const contactFieldId = (field, index) => `contacts-${field}-${String(index + 1).padStart(2, '0')}`;

const handleContactsEdit = () => {
  contactsError.value = '';
  contactsFieldErrors.value = {};
  draftContacts.value =
    normalizedContacts.value.length > 0
      ? normalizedContacts.value.map(contact => ({
          uuid: contact.uuid,
          label: contact.label,
          value: contact.value,
          errors: { label: '', value: '' },
          inputErrors: { label: false, value: false },
        }))
      : [makeBlankContact()];
  isEditingContacts.value = true;
};

const addContactEntry = () => {
  if (draftContacts.value.length >= MAX_CONTACTS) {
    contactsFieldErrors.value = {
      ...contactsFieldErrors.value,
      contacts: `You can add up to ${MAX_CONTACTS} contact rows.`,
    };
    return;
  }

  contactsFieldErrors.value = {
    ...contactsFieldErrors.value,
    contacts: '',
  };
  draftContacts.value.push(makeBlankContact());
};

const removeContactEntry = index => {
  draftContacts.value.splice(index, 1);
  if (draftContacts.value.length < MAX_CONTACTS) {
    contactsFieldErrors.value = {
      ...contactsFieldErrors.value,
      contacts: '',
    };
  }
};

const cancelContactsEdit = () => {
  draftContacts.value = [];
  contactsError.value = '';
  contactsFieldErrors.value = {};
  isEditingContacts.value = false;
};

const applyContactValidationErrors = errors => {
  contactsFieldErrors.value = {
    contacts: errors.contacts || '',
  };

  Object.entries(errors).forEach(([key, message]) => {
    const match = key.match(/^contacts\.(\d+)\.(label|value)$/);
    if (!match) return;

    const index = Number(match[1]);
    const field = match[2];
    if (draftContacts.value[index]) {
      draftContacts.value[index].errors[field] = message;
      draftContacts.value[index].inputErrors[field] = Boolean(message);
    }
  });
};

const validateDraftContacts = () => {
  let hasErrors = false;

  contactsFieldErrors.value = {};
  if (draftContacts.value.length > MAX_CONTACTS) {
    contactsFieldErrors.value.contacts = `You can add up to ${MAX_CONTACTS} contact rows.`;
    hasErrors = true;
  }

  draftContacts.value.forEach(entry => {
    const labelEmpty = entry.label.trim() === '';
    const valueEmpty = entry.value.trim() === '';
    entry.errors.label = labelEmpty ? '*Contact label is required.' : '';
    entry.errors.value = valueEmpty ? '*Contact value is required.' : '';
    entry.inputErrors.label = labelEmpty;
    entry.inputErrors.value = valueEmpty;
    if (labelEmpty || valueEmpty) {
      hasErrors = true;
    }
  });

  return !hasErrors;
};

const saveContactsEdit = async () => {
  contactsError.value = '';

  if (!validateDraftContacts()) return;

  isSavingContacts.value = true;

  try {
    await profileStore.saveCurrentProfile({
      contacts: draftContacts.value.map(entry => ({
        label: entry.label,
        value: entry.value,
      })),
    });
    draftContacts.value = [];
    contactsFieldErrors.value = {};
    isEditingContacts.value = false;
  } catch (error) {
    if (error.errors) {
      applyContactValidationErrors(error.errors);
    }

    contactsError.value =
      error.errors && Object.keys(error.errors).length > 0
        ? ''
        : error.message || 'Failed to save your contacts. Please try again.';
  } finally {
    isSavingContacts.value = false;
  }
};
</script>

<template>
  <div class="profile-page_meta-grid-container">
    <ul class="profile-page_meta-grid">
      <li v-if="hasDirectoryBox" class="profile-page_meta-grid-item directory">
        <template v-if="departmentText">
          <p class="profile-page_meta-row-label">Department</p>
          <p class="profile-page_meta-row-value">{{ departmentText }}</p>
        </template>
        <template v-if="researchAreasText">
          <p class="profile-page_meta-row-label">Research Areas</p>
          <p class="profile-page_meta-row-value">{{ researchAreasText }}</p>
        </template>
        <template v-if="titlesText">
          <p class="profile-page_meta-row-label">Titles</p>
          <p class="profile-page_meta-row-value">{{ titlesText }}</p>
        </template>
        <template v-if="programsText">
          <p class="profile-page_meta-row-label">Programs</p>
          <p class="profile-page_meta-row-value">{{ programsText }}</p>
        </template>
      </li>

      <li class="profile-page_meta-grid-item audience">
        <p class="profile-page_meta-row-label">Audience Group</p>
        <ul class="profile-page_meta-audience">
          <li>{{ audienceLabel }}</li>
        </ul>
        <p class="profile-page_meta-audience-disclaimer">
          {{ AUDIENCE_GROUP_DESCRIPTION }}
          <a href="/faq">Learn more</a>
        </p>
      </li>

      <li v-if="hasContactBox" class="profile-page_meta-grid-item contact">
        <template v-if="officesText">
          <p class="profile-page_meta-row-label">Office</p>
          <p class="profile-page_meta-row-value">{{ officesText }}</p>
        </template>
        <template v-if="person.officeAddress">
          <p class="profile-page_meta-row-label">Office Address</p>
          <p class="profile-page_meta-row-value">{{ person.officeAddress }}</p>
        </template>
        <template v-if="email">
          <p class="profile-page_meta-row-label">Email</p>
          <p class="profile-page_meta-row-value is-email">
            <a :href="emailHref">{{ email }}</a>
          </p>
        </template>
        <template v-if="person.phone">
          <p class="profile-page_meta-row-label">Phone</p>
          <p class="profile-page_meta-row-value">
            <a :href="phoneHref">{{ person.phone }}</a>
          </p>
        </template>
      </li>

      <li class="profile-page_meta-grid-item additional-info">
        <template v-if="!isEditingContacts">
          <p v-if="normalizedContacts.length === 0" class="profile-page_meta-additional-info-desc">
            Add additional contact information like your website, social media handles, phone
            numbers, etc.
          </p>
          <ul v-else class="profile-page_contacts-saved-list">
            <li
              v-for="(contact, i) in normalizedContacts"
              :key="contact.uuid || i"
              class="profile-page_contacts-saved-entry"
            >
              <p class="profile-page_contacts-saved-label">{{ contact.label }}</p>
              <p class="profile-page_contacts-saved-value">{{ contact.value }}</p>
            </li>
          </ul>
          <div class="profile-page_meta-additional-info-button">
            <button type="button" @click="handleContactsEdit">Edit</button>
          </div>
        </template>

        <div v-else class="profile-page_contacts-editing">
          <p class="profile-page_contacts-title">Additional Contact Information</p>
          <div
            v-for="(entry, i) in draftContacts"
            :key="entry.uuid || i"
            class="profile-page_contacts-edit-entry"
          >
            <div class="profile-page_contacts-entry-head">
              <label class="profile-page_contacts-sublabel" :for="contactFieldId('label', i)"
                >Field name*</label
              >
              <button
                type="button"
                class="profile-page_contacts-remove-btn"
                aria-label="Remove entry"
                :disabled="isSavingContacts"
                @click="removeContactEntry(i)"
              >
                <CloseIcon />
              </button>
            </div>
            <input
              :id="contactFieldId('label', i)"
              v-model="entry.label"
              type="text"
              class="profile-page_contacts-input"
              :class="{ 'is-error': entry.inputErrors.label }"
              placeholder="Field name (URL, Phone, …)"
              :aria-invalid="Boolean(entry.inputErrors.label)"
            />
            <p class="profile-page_contacts-helper" :class="{ 'is-error': entry.errors.label }">
              {{ entry.errors.label }}
            </p>
            <label class="profile-page_contacts-sublabel" :for="contactFieldId('value', i)"
              >Field text*</label
            >
            <input
              :id="contactFieldId('value', i)"
              v-model="entry.value"
              type="text"
              class="profile-page_contacts-input"
              :class="{ 'is-error': entry.inputErrors.value }"
              placeholder="Field text"
              :aria-invalid="Boolean(entry.inputErrors.value)"
            />
            <p class="profile-page_contacts-helper" :class="{ 'is-error': entry.errors.value }">
              {{ entry.errors.value }}
            </p>
          </div>
          <p class="profile-page_required-field-label">*indicates required field</p>
          <button
            type="button"
            class="profile-page_contacts-add-btn"
            :disabled="isSavingContacts"
            @click="addContactEntry"
          >
            Add
          </button>
          <p
            v-if="contactsFieldErrors.contacts"
            class="profile-page_contacts-helper is-error"
            role="alert"
          >
            {{ contactsFieldErrors.contacts }}
          </p>
          <p v-if="contactsError" class="profile-page_contacts-helper is-error" role="alert">
            {{ contactsError }}
          </p>
          <div class="profile-page_contacts-actions">
            <button
              type="button"
              class="profile-page_contacts-action-btn profile-page_contacts-cancel"
              :disabled="isSavingContacts"
              @click="cancelContactsEdit"
            >
              Cancel
            </button>
            <button
              type="button"
              class="profile-page_contacts-action-btn profile-page_contacts-save"
              :disabled="isSavingContacts"
              @click="saveContactsEdit"
            >
              {{ isSavingContacts ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>
